import { useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Paperclip } from "lucide-react";
import { DeleteEntryDialog } from "../shared/DeleteEntryDialog";
import { formatIst } from "../shared/formatIst";
import { BalanceLabel, StatusBadge, fmtDate, money } from "./imprestShared";

async function openAttachment(entryId) {
  try {
    const res = await client.get(`/payment-desk/imprest/bills/${entryId}/attachment`, { responseType: "blob" });
    window.open(URL.createObjectURL(res.data), "_blank");
  } catch (err) {
    toast.error(getErrorMessage(err));
  }
}

function AdvancesTable({ rows, onDelete, prefix }) {
  return (
    <Table data-testid={`${prefix}-advances-table`}>
      <TableHeader>
        <TableRow><TableHead>Ref</TableHead><TableHead>Date</TableHead><TableHead>Mode</TableHead><TableHead>Entity</TableHead><TableHead>Reference</TableHead><TableHead>Remarks</TableHead><TableHead className="text-right">Amount</TableHead>{onDelete && <TableHead />}</TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow><TableCell colSpan={8} className="text-center py-5 text-slate-400">No advances.</TableCell></TableRow>
        ) : rows.map((r) => (
          <TableRow key={r.id} data-testid={`imprest-advance-row-${r.entry_id}`}>
            <TableCell className="font-mono text-xs font-semibold">{r.entry_id}</TableCell>
            <TableCell className="text-xs">{fmtDate(r.payment_date)}</TableCell>
            <TableCell className="text-xs">{r.mode}</TableCell>
            <TableCell className="text-xs">{r.entity_name || "—"}</TableCell>
            <TableCell className="text-xs">{r.reference || "—"}</TableCell>
            <TableCell className="text-xs">{r.purpose || "—"}</TableCell>
            <TableCell className="text-right font-medium">{money(r.amount)}</TableCell>
            {onDelete && (
              <TableCell className="text-right">
                {r.can_delete && <Button size="sm" variant="destructive" data-testid={`imprest-advance-delete-${r.entry_id}`} onClick={() => onDelete({ kind: "advances", row: r })}>Delete</Button>}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function BillsTable({ rows, onDelete, prefix }) {
  return (
    <Table data-testid={`${prefix}-bills-table`}>
      <TableHeader>
        <TableRow><TableHead>Ref</TableHead><TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Attributed To</TableHead><TableHead>Remarks</TableHead><TableHead>Bill</TableHead><TableHead className="text-right">Amount</TableHead>{onDelete && <TableHead />}</TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow><TableCell colSpan={8} className="text-center py-5 text-slate-400">No bills.</TableCell></TableRow>
        ) : rows.map((r) => (
          <TableRow key={r.id} data-testid={`imprest-bill-row-${r.entry_id}`}>
            <TableCell className="font-mono text-xs font-semibold">{r.entry_id}</TableCell>
            <TableCell className="text-xs">{fmtDate(r.bill_date)}</TableCell>
            <TableCell className="text-xs">{r.category_name}</TableCell>
            <TableCell className="text-xs">{r.site_label || r.equipment_label || "—"}</TableCell>
            <TableCell className="text-xs">{r.remarks || "—"}</TableCell>
            <TableCell>
              {r.attachment ? (
                <button type="button" className="flex items-center gap-1 text-xs text-sky-700 underline" data-testid={`imprest-bill-attachment-${r.entry_id}`} onClick={() => openAttachment(r.entry_id)}>
                  <Paperclip className="h-3 w-3" /> View
                </button>
              ) : <span className="text-xs text-slate-400">—</span>}
            </TableCell>
            <TableCell className="text-right font-medium">{money(r.amount)}</TableCell>
            {onDelete && (
              <TableCell className="text-right">
                {r.can_delete && <Button size="sm" variant="destructive" data-testid={`imprest-bill-delete-${r.entry_id}`} onClick={() => onDelete({ kind: "bills", row: r })}>Delete</Button>}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const Panel = ({ title, total, children, testId }) => (
  <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={testId}>
    <div className="flex justify-between items-center px-4 py-2 border-b border-slate-200 bg-slate-50">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <span className="text-sm font-semibold">{money(total)}</span>
    </div>
    {children}
  </div>
);

// Reconciliation view: current period (advances + bills + balance) and settled periods below.
export default function ImprestEmployeeView({ employeeId, reloadKey, onBack, onLogAdvance, onAddBill, onSettle, onChanged }) {
  const [data, setData] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () =>
    client.get(`/payment-desk/imprest/employees/${employeeId}`).then((res) => setData(res.data)).catch((err) => toast.error(getErrorMessage(err)));

  useEffect(() => { load(); }, [employeeId, reloadKey]);

  if (!data) return <div className="py-8 text-center text-slate-400">Loading...</div>;
  const cur = data.current;

  return (
    <div className="space-y-4" data-testid="imprest-employee-view">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="imprest-back-button"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <h2 className="text-lg font-semibold text-slate-900" data-testid="imprest-employee-name">{data.employee_name} <span className="text-sm font-normal text-slate-500">({data.employee_id})</span></h2>
        <StatusBadge status={data.status} testId="imprest-employee-status" />
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onLogAdvance(employeeId)} data-testid="imprest-view-log-advance-button">Log Advance</Button>
          <Button size="sm" variant="outline" onClick={() => onAddBill(employeeId)} data-testid="imprest-view-add-bill-button">Add Bill</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={data.status === "Settled"} onClick={() => onSettle({ employeeId, employeeName: data.employee_name, balance: cur.balance })} data-testid="imprest-mark-settled-button">Mark Settled</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 rounded-lg border border-slate-200 bg-white p-4">
        <div><div className="text-xs text-slate-500">Advances Given</div><div className="text-lg font-semibold" data-testid="imprest-view-total-advances">{money(cur.total_advances)}</div></div>
        <div><div className="text-xs text-slate-500">Bills Tallied</div><div className="text-lg font-semibold" data-testid="imprest-view-total-bills">{money(cur.total_bills)}</div></div>
        <div><div className="text-xs text-slate-500">Balance</div><BalanceLabel balance={cur.balance} testId="imprest-view-balance" large /></div>
      </div>

      <Panel title="Advances" total={cur.total_advances} testId="imprest-current-advances"><AdvancesTable rows={cur.advances} onDelete={setDeleteTarget} prefix="imprest-current" /></Panel>
      <Panel title="Bills" total={cur.total_bills} testId="imprest-current-bills"><BillsTable rows={cur.bills} onDelete={setDeleteTarget} prefix="imprest-current" /></Panel>

      {data.periods.length > 0 && (
        <div className="space-y-3" data-testid="imprest-settled-periods">
          <h3 className="text-sm font-semibold text-slate-700">Settled periods</h3>
          {data.periods.map((p) => (
            <details key={p.settlement_id} className="rounded-lg border border-slate-200 bg-white" data-testid={`imprest-period-${p.settlement_id}`}>
              <summary className="cursor-pointer px-4 py-2 text-sm flex flex-wrap gap-x-4">
                <span className="font-medium">Settled {formatIst(p.settled_at)} by {p.settled_by_name}</span>
                <span className="text-slate-500">Advances {money(p.total_advances)} · Bills {money(p.total_bills)} · Balance {money(p.balance_at_settlement)}</span>
                <span className="text-slate-600 italic">“{p.note}”</span>
              </summary>
              <div className="space-y-3 p-3">
                <AdvancesTable rows={p.advances} prefix={`imprest-period-${p.settlement_id}`} />
                <BillsTable rows={p.bills} prefix={`imprest-period-${p.settlement_id}`} />
              </div>
            </details>
          ))}
        </div>
      )}

      {deleteTarget && (
        <DeleteEntryDialog
          title={`Delete ${deleteTarget.row.entry_id}?`}
          details={[["Ref", deleteTarget.row.entry_id], ["Amount", money(deleteTarget.row.amount)], ["Date", fmtDate(deleteTarget.row.payment_date || deleteTarget.row.bill_date)]]}
          endpoint={deleteTarget.row.delete_endpoint || `/payment-desk/imprest/${deleteTarget.kind}/${deleteTarget.row.entry_id}/delete`}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); load(); onChanged(); }}
        />
      )}
    </div>
  );
}
